export default class Collapsible {
  constructor({
    wrap,
    holder,
    opener,
    slide,
    btnBack,
    activeClass = 'slide-opened',
    dropActiveClass = 'drop-active',
    breakpoints = [
      { width: 1280, behavior: 'disable' },
      { width: 1024, behavior: 'firstLevel' },
      { width: 0, behavior: 'allLevels' },
    ],
  }) {
    this.holder = holder;
    this.wrap = holder.closest(wrap);
    this.opener = opener;
    this.slide = slide;
    this.btnBack = btnBack;
    this.activeClass = activeClass;
    this.dropActiveClass = dropActiveClass;
    this.breakpoints = breakpoints;
    this.currentBehavior = null;
    this.isOpened = false;
    this.focusableElements = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

    this.init();
  }

  events() {
    this.handleClick = this.clickHandler.bind(this);
    this.handleKeyPress = this.keyPressHandler.bind(this);
    this.keyPressHandler = this.keyPress.bind(this);
    this.outsideClickHandler = this.outsideClick.bind(this);
  }

  init() {
    if (typeof this.holder === 'string') {
      this.holder = document.querySelector(this.holder);
    }

    this.slide = this.holder.querySelector(`:scope ${this.slide}`);
    this.opener = this.holder.querySelector(`:scope ${this.opener}`);

    if (!this.opener) {
      return;
    }

    this.btnBack = this.slide?.querySelector(`:scope ${this.btnBack}`);

    if (this.btnBack && !this.btnBack.querySelector('.sub-menu-title')) {
      const titleSpan = document.createElement('span');
      titleSpan.className = 'sub-menu-title';

      const parentMenuItem = this.btnBack.closest('.sub-menu-holder')?.previousElementSibling;
      const menuTitle = parentMenuItem?.textContent?.trim() || '';

      titleSpan.textContent = menuTitle;
      this.btnBack.appendChild(titleSpan);
    }

    this.events();

    const mediaQueryList = this.breakpoints.map((bp) => window.matchMedia(`(min-width: ${bp.width}px)`));

    mediaQueryList.forEach((mql) => {
      mql.addEventListener('change', this.handleMediaChange.bind(this));
    });

    // Initial check
    this.handleMediaChange();
  }

  handleMediaChange() {
    const windowWidth = window.innerWidth;
    let behavior = 'allLevels';

    for (let bp of this.breakpoints) {
      if (windowWidth >= bp.width) {
        behavior = bp.behavior;
        break;
      }
    }

    if (behavior !== this.currentBehavior) {
      this.currentBehavior = behavior;
      this.updateBehavior();
    }
  }

  updateBehavior() {
    switch (this.currentBehavior) {
      case 'disable':
        this.disableCollapsible();
        break;
      case 'firstLevel':
        this.enableCollapsible(true);
        break;
      case 'allLevels':
        this.enableCollapsible(false);
        break;
      default:
        this.disableCollapsible();
    }
  }

  enableCollapsible(isFirstLevelOnly) {
    if (isFirstLevelOnly && this.isNested()) {
      this.disableCollapsible();
      return;
    }

    if (!this.opener) {
      return;
    }

    this.opener.setAttribute('aria-expanded', 'false');
    this.opener.setAttribute('aria-haspopup', 'true');

    this.slide.style.overflow = 'hidden';

    this.opener.addEventListener('click', this.handleClick);
    this.opener.addEventListener('keyup', this.handleKeyPress);

    if (this.btnBack) {
      this.btnBack.addEventListener('click', this.handleClick);
      this.btnBack.addEventListener('keyup', this.handleKeyPress);
    }
  }

  disableCollapsible() {
    this.holder.classList.remove(this.activeClass);

    if (this.opener) {
      this.opener.removeAttribute('aria-expanded');
      this.opener.removeAttribute('aria-haspopup');
    }

    if (this.slide) {
      this.slide.style.height = 'auto';
      this.slide.style.overflow = '';
    }

    this.destroy();
  }

  isNested() {
    return this.holder.closest('.sub-menu-holder') !== null;
  }

  setInitialHeight() {
    this.slide.style.height = '0';

    var contentHeight = this.slide.scrollHeight + 'px';
    this.slide.style.height = contentHeight;
  }

  showSlide() {
    this.holder.classList.add(this.activeClass);
    this.isOpened = true;
    if (this.opener) {
      this.opener.setAttribute('aria-expanded', this.isOpened);
    }
    this.slide.style.height = '';

    this.focusableContent = [...this.slide.querySelectorAll(this.focusableElements)].filter((item) => item.getBoundingClientRect().height !== 0);

    document.addEventListener('click', this.outsideClickHandler);
    this.holder.addEventListener('keydown', this.keyPressHandler);
  }

  hideSlide() {
    this.holder.classList.remove(this.activeClass);
    this.isOpened = false;

    if (this.opener) {
      this.opener.setAttribute('aria-expanded', this.isOpened);
    }

    this.setInitialHeight();

    document.removeEventListener('click', this.outsideClickHandler);
    this.holder.removeEventListener('keydown', this.keyPressHandler);
  }

  clickHandler(e) {
    e.preventDefault();

    if (this.isOpened) {
      this.hideSlide();
    } else {
      this.showSlide();
    }

    const event = new CustomEvent('opener-clicked', {
      detail: {
        isOpened: this.isOpened,
        holder: this.holder,
      },
      bubbles: true,
      cancelable: true,
    });

    this.opener.dispatchEvent(event);
  }

  outsideClick(e) {
    if (this.isOpened && !this.holder.contains(e.target)) {
      this.hideSlide();
    }
  }

  keyPress(e) {
    const lastFocusableElement = this.focusableContent[this.focusableContent.length - 1];

    switch (e.key) {
      case 'Escape':
        if (this.holder.querySelector('[aria-expanded="true"]')) {
          e.stopPropagation();
        }

        this.hideSlide();
        document.removeEventListener('keydown', this.keyPressHandler);
        break;

      case 'Tab':
        if (e.shiftKey) {
          if (document.activeElement === this.opener) {
            if (this.isOpened) this.hideSlide();
          }
        } else {
          if (document.activeElement === lastFocusableElement && !document.activeElement.getAttribute('aria-haspopup')) {
            if (this.isOpened) this.hideSlide();
          }
        }
        break;
    }
  }

  keyPressHandler(e) {
    switch (e.key) {
      case ' ':
        e.preventDefault();

        if (this.isOpened) {
          this.hideSlide();
        } else {
          this.showSlide();
        }
    }
  }

  destroy() {
    if (this.opener) {
      this.opener.removeAttribute('aria-expanded');
      this.opener.removeAttribute('aria-haspopup');
      this.opener.removeEventListener('click', this.handleClick);
      this.opener.removeEventListener('keyup', this.handleKeyPress);
    }
    if (this.holder) {
      this.holder.removeEventListener('keydown', this.keyPressHandler);
    }
    if (this.slide) {
      this.slide.style.overflow = '';
      this.slide.style.height = 'auto';
    }

    this.opener = null;
  }
}
